import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  entryKeyOf,
  headingsOf,
  navigationOf,
  SaphirNavigationAccent,
  SaphirNavigationCommands,
  SaphirNavigationHeading,
  SaphirNavigationItem,
} from '@saphir/module-contract';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { filter, map } from 'rxjs';

import { rendersArrangedMenu } from '../../core/modules/arranged-menu';
import { MODULE_ROUTE_PREFIX } from '../../core/modules/module-routes';
import { CompanyContextService } from '../../services/company-context.service';
import { LicenseContextService, isWithheld } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { UserMenu } from '../user-menu/user-menu';

/**
 * One module's block in the sidebar: its own link, then its entries.
 *
 * `items` and `headings` are alternatives, never both. A module sends one
 * shape or the other; `headings` is non-empty only for a module allowed an
 * arranged section that is actually sending one, so the template can branch
 * on it alone.
 */
interface SidebarModule {
  readonly id: string;
  readonly titleKey: string;
  readonly icon: string;
  readonly items: readonly SaphirNavigationItem[];
  readonly headings: readonly SaphirNavigationHeading[];
}

/**
 * The application's one navigation, always on screen — the dark column of the
 * design system. Top to bottom: the brand, the company the user works as, the
 * destinations (Dashboard first, then every available module with the entries
 * it declared through the contract), and the signed-in user with the way out.
 *
 * Company and user live here rather than in the header: both are context that
 * holds for the whole session, and the column is the one surface every screen
 * shares. Switching the company re-evaluates every permission the modules
 * check.
 *
 * Two orthogonal folds. Per module: collapsed by default, the open one unfolds
 * itself, the chevron overrides, entering a module clears the override. For the
 * whole sidebar: the header's burger shrinks it to an icon rail — labels and
 * chevrons give way to one icon per destination, named by a tooltip and an
 * aria-label that stays when the text goes. A module's sub-entries survive the
 * rail as a flyout: the sublist stays in the DOM and appears beside the icon
 * while the pointer or keyboard focus is in the module, titled after its
 * module. Picking a destination dismisses it at once — the pointer is still
 * over the module after a click, and a panel that lingers there covers the
 * very screen the click asked for. It reopens on the next enter.
 *
 * The shell builds the links — a module's routes are relative to wherever it is
 * mounted, and where that is (`/modules/<id>`) is the shell's decision, made in
 * exactly one other place (`module-routes.ts`). Labels are translation keys
 * resolved against the dictionaries the registry merged at startup, so a
 * module's entries are translated before the module has ever been opened.
 */
@Component({
  selector: 'app-shell-sidebar',
  imports: [
    NgTemplateOutlet,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    Tooltip,
    Select,
    UserMenu,
  ],
  templateUrl: './shell-sidebar.html',
  styleUrl: './shell-sidebar.scss',
  host: {
    id: 'shell-sidebar',
    '[class.sidebar--rail]': 'rail()',
    // Escape closes an open arrange panel; a click anywhere that is not
    // inside one closes it too (FR-026). Both put focus back on the control
    // that opened it — see `closePanel`.
    '(keydown.escape)': 'closePanel()',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class ShellSidebar {
  private readonly registry = inject(ModuleRegistryService);
  private readonly router = inject(Router);

  protected readonly prefix = MODULE_ROUTE_PREFIX;

  protected readonly companies = inject(CompanyContextService);

  private readonly sidebarState = inject(SidebarStateService);

  /** Icon rail instead of the full column — toggled from the header. */
  protected readonly rail = this.sidebarState.collapsed;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** The module the user is currently inside, from the address. */
  private readonly activeId = computed(() => moduleIdFromUrl(this.url()));

  /**
   * Explicit chevron choices, by module id. Re-derived from the active module:
   * entering a module drops whatever was toggled for it, so the default —
   * "the module I am in is open" — applies again.
   */
  private readonly overrides = linkedSignal<string | undefined, Record<string, boolean>>({
    source: this.activeId,
    computation: (active, previous) => {
      const kept = { ...(previous?.value ?? {}) };

      if (active) {
        delete kept[active];
      }

      return kept;
    },
  });

  private readonly licenses = inject(LicenseContextService);

  /** Whether UMC has answered — until then the module list shows placeholders. */
  protected readonly licensesSettled = computed(() => this.licenses.settled());

  /** One placeholder per module that may appear, so the column does not jump. */
  protected readonly skeletonRows = computed(() =>
    Array.from({ length: this.registry.availableModules().length }),
  );

  /**
   * Unlicensed modules are not offered here — the dashboard tile explains.
   * Nothing is offered before UMC has answered: every verdict reads "open"
   * until then.
   */
  protected readonly modules = computed<SidebarModule[]>(() =>
    this.registry
      .availableModules()
      .filter(() => this.licenses.settled())
      .filter((module) => !this.licenses.isBlocked(module.id))
      .map((module) => {
        const definition = this.registry.definitionOf(module.id);

        // Two questions in this order, and the order is the point: is this
        // module ALLOWED an arranged section (one named place, see
        // `arranged-menu.ts`), and is it actually SENDING one. A release that
        // publishes a plain tree renders as a plain tree, never as an empty
        // section (FR-029b).
        const headings = rendersArrangedMenu(module.id) ? headingsOf(definition) : [];

        return {
          id: module.id,
          titleKey: module.manifest?.titleKey ?? module.id,
          icon: module.manifest?.icon ?? 'pi pi-box',
          // Flattened entries stay available whichever shape arrived: the
          // rail flyout, the fold rules and `containsActive` all walk them.
          items: navigationOf(definition),
          headings,
        };
      }),
  );

  /**
   * The companies offered in the switcher, each marked with whether it may be
   * picked while standing in this module.
   *
   * A company without a licence for the module the user is currently in is
   * offered but not selectable: switching to it would land on a screen the
   * shell has to withhold, bouncing the user back to the dashboard. Better to
   * say so before the click than to undo it after.
   *
   * Outside a module every company is selectable — there is nothing to be
   * unlicensed FOR.
   */
  protected readonly companyOptions = computed(() => {
    const moduleId = this.activeId();

    return this.companies.memberships().map((membership) => ({
      ...membership,
      unlicensed:
        moduleId !== undefined &&
        isWithheld(this.licenses.licenseStateFor(membership.companyId, moduleId)),
    }));
  });

  /** Up to two initials of the customer's name, for the tile beside it. */
  protected readonly customerInitials = computed(() => {
    const name = this.companies.activeCustomerName() ?? '';
    const words = name.split(/\s+/).filter((word) => word.length > 0);
    const initials = words
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');

    return initials || '?';
  });

  constructor() {
    // The switcher judges every company, not just the active one, so their
    // catalogues have to be in before the list is opened.
    effect(() => {
      this.licenses.prefetchLicenses(this.companies.memberships().map((m) => m.companyId));
    });
  }

  protected switchCompany(companyId: number): void {
    this.companies.setActiveCompany(companyId);
  }

  /** The module whose rail flyout is showing — pointer or focus is inside it. */
  protected readonly flyoutOpen = signal<string | null>(null);

  /**
   * Below this width the column floats over the content rather than beside
   * it; the stylesheet uses the same threshold.
   */
  private static readonly COMPACT_WIDTH = '(max-width: 60rem)';

  /**
   * On a narrow viewport the column is an overlay, so picking a destination
   * has to put it away — otherwise it covers the very screen the click asked
   * for (FR-025). On a wide one it stays: the column is permanent furniture
   * there, and collapsing it on every click would be maddening.
   */
  protected onDestinationPicked(moduleId: string): void {
    this.hideFlyout(moduleId);

    if (!this.rail() && window.matchMedia?.(ShellSidebar.COMPACT_WIDTH).matches) {
      this.sidebarState.toggle();
    }
  }

  protected showFlyout(moduleId: string): void {
    this.flyoutOpen.set(moduleId);
  }

  protected hideFlyout(moduleId: string): void {
    if (this.flyoutOpen() === moduleId) {
      this.flyoutOpen.set(null);
    }
  }

  /** Focus moves inside the module keep the flyout; only leaving closes it. */
  protected onFlyoutFocusOut(event: FocusEvent, moduleId: string): void {
    const host = event.currentTarget as HTMLElement;

    if (!(event.relatedTarget instanceof Node && host.contains(event.relatedTarget))) {
      this.hideFlyout(moduleId);
    }
  }

  /**
   * Explicit chevron choices on groups inside a module's navigation, keyed by
   * the group's path (`<module>/<label>/<label>`), each stamped with the
   * address it was made on. A choice holds while the user stays on that
   * screen; navigating INTO a group re-opens it even if it was folded by hand
   * before — the menu must always show where the user is. Opening a group by
   * hand is kept until a navigation elsewhere folds it again by the default
   * rule, which is what a fold-out menu is expected to do.
   *
   * Persisted (FR-023). In memory alone the state died whenever the column
   * was put away and came back, and users read that as a bug — they had
   * folded something and it was open again. Only the choice is stored; the
   * address it was made on belongs to this session and is restored empty, so
   * after a reload a manual fold holds until the user navigates into it.
   */
  private readonly groupOverrides = signal<Record<string, { open: boolean; url: string }>>(
    restoreFolds(),
  );

  /**
   * Open when the current screen is inside the group, or when the user opened
   * it; folded by hand only until the next navigation into it. The rail's
   * flyout shows everything — there is no room to fold.
   */
  protected groupOpen(moduleId: string, key: string, group: SaphirNavigationItem): boolean {
    if (this.rail()) {
      return true;
    }

    const override = this.groupOverrides()[key];
    const holdsActive = this.containsActive(moduleId, group);

    if (override === undefined) {
      return holdsActive;
    }

    // Both kinds of choice expire when the user walks away from the screen
    // they were made on — a group is a fold-out, not a drawer that stays open
    // forever. Opening Konstanten to find one screen must not leave its 34
    // entries in the column for every session after.
    //
    // A heading is different: it is the user's own section and stays as they
    // left it (FR-023), which is why `headingOpen` has no such expiry.
    if (override.url !== this.currentPath()) {
      return holdsActive;
    }

    return override.open;
  }

  protected toggleGroup(key: string, moduleId: string, group: SaphirNavigationItem): void {
    const next = !this.groupOpen(moduleId, key, group);

    this.setFold(key, next);
  }

  /** Record a fold choice and remember it past this session (FR-023). */
  private setFold(key: string, open: boolean): void {
    this.groupOverrides.update((overrides) => ({
      ...overrides,
      [key]: { open, url: this.currentPath() },
    }));

    persistFolds(this.groupOverrides());
  }

  /** The address without query and fragment — what the navigation model is compared against. */
  private currentPath(): string {
    return this.url().split(/[?#]/)[0];
  }

  /**
   * How ONE entry is identified here: the contract's rule, re-exported so the
   * template can reach it.
   *
   * Everything keyed to an entry goes through this — `track`, fold state —
   * because the alternative was the label key, and label keys are generated
   * from labels: Fakt's catalogue has `Konstantendaten` four times. Equal
   * track keys are an Angular error (NG0955) and equal fold keys silently
   * fold the wrong row.
   */
  protected entryKey(item: SaphirNavigationItem): string {
    return entryKeyOf(item);
  }

  /**
   * The fold key of a group: its own key under the path of its ancestors.
   *
   * Scoped by path rather than taken bare, because a module may legitimately
   * reuse an id in two branches of its tree; the path keeps two such groups
   * apart while an id keeps two LABELS apart. Both are needed.
   */
  protected groupKey(path: string, item: SaphirNavigationItem): string {
    return `${path}/${entryKeyOf(item)}`;
  }

  /**
   * The item's live counter, when the module feeds one.
   *
   * A number or a short text, rendered AS GIVEN: `1-32` is a range meaning
   * one project holding 32 documents, and `1` would say something else. The
   * host does not parse it and does not aggregate — a count rolled up onto a
   * folded heading is a number the user cannot trace to a screen.
   *
   * An empty string is treated as no badge: a pill with nothing in it is
   * noise, and a module sending one means it has nothing to say.
   */
  protected badgeOf(item: SaphirNavigationItem): number | string | null {
    const count = item.badge?.();

    if (typeof count === 'number') {
      return count;
    }

    return typeof count === 'string' && count.trim() !== '' ? count : null;
  }

  /**
   * Is the heading open?
   *
   * The same rule as a group: open when it holds the current screen, the
   * chevron overrides, and a manual fold loses to navigating into it. The
   * rail's flyout shows everything — there is no room to fold.
   */
  protected headingOpen(moduleId: string, heading: SaphirNavigationHeading): boolean {
    if (this.rail()) {
      return true;
    }

    // An untitled heading has no row to fold it with, so its entries are always shown.
    if (this.isUntitled(heading)) {
      return true;
    }

    const key = this.headingKey(moduleId, heading);
    const override = this.groupOverrides()[key];
    const holdsActive = heading.entries.some((entry) => this.containsActive(moduleId, entry));

    if (override === undefined) {
      // A heading holding a single entry opens by default: folding one row
      // behind another row is a click that buys nothing, and the module uses
      // exactly this shape for the way back out of a sub-module — which has
      // to be visible, not hidden behind a chevron.
      return holdsActive || heading.entries.length === 1;
    }

    if (!override.open && holdsActive && override.url !== this.currentPath()) {
      return true;
    }

    return override.open;
  }

  /**
   * A heading without a title: its entries stand directly in the column, with no heading row.
   *
   * Fakt sends its catch-all ("Weitere Funktionen") untitled on purpose — its own sidebar shows
   * an unlabelled section's screens directly, not as a group. Rendered as a heading it was an
   * empty row with a bare chevron that folded the screens away.
   */
  protected isUntitled(heading: SaphirNavigationHeading): boolean {
    const title = heading.title.trim();
    return title === '';
  }

  protected toggleHeading(moduleId: string, heading: SaphirNavigationHeading): void {
    const next = !this.headingOpen(moduleId, heading);

    this.setFold(this.headingKey(moduleId, heading), next);
  }

  /**
   * The path a heading's entries are keyed under. Headings and groups share
   * one fold store, so the key is prefixed to keep a heading id from
   * colliding with a group of the same name.
   */
  protected headingKey(moduleId: string, heading: SaphirNavigationHeading): string {
    return `${moduleId}/heading:${heading.id}`;
  }

  /**
   * The accent an entry wears: its own, or the heading's when that heading
   * extends its accent to what it holds (`accentOnEntries`).
   *
   * An entry's own always wins — it says what the SCREEN is, where the
   * heading's says what the group is for.
   */
  protected accentOf(
    item: SaphirNavigationItem,
    heading?: SaphirNavigationHeading,
  ): string | null {
    const accent = item.accent ?? (heading?.accentOnEntries ? heading.accent : undefined);

    return accent ?? null;
  }

  // ── Arranging, from inside the shell ──────────────────────────────────────

  /**
   * The module's command channel, or `undefined`.
   *
   * Feature-detected per FUNCTION at each call site, not once for the whole
   * channel: a module may support moving entries and not renaming headings,
   * and a control that is visible but does nothing is worse than no control
   * — the user cannot tell a broken menu from a stubborn one.
   */
  private commandsFor(moduleId: string): SaphirNavigationCommands | undefined {
    return this.registry.definitionOf(moduleId)?.navigationCommands;
  }

  /** Is there any way at all to rearrange this module's menu? */
  protected canArrange(moduleId: string): boolean {
    const commands = this.commandsFor(moduleId);

    return (
      !this.rail() &&
      rendersArrangedMenu(moduleId) &&
      (commands?.moveEntry !== undefined || commands?.createHeading !== undefined)
    );
  }

  protected canMoveEntries(moduleId: string): boolean {
    return !this.rail() && this.commandsFor(moduleId)?.moveEntry !== undefined;
  }

  protected canEditHeading(moduleId: string, heading: SaphirNavigationHeading): boolean {
    if (this.rail() || heading.fixed) {
      return false;
    }

    const commands = this.commandsFor(moduleId);

    return (
      commands?.renameHeading !== undefined ||
      commands?.moveHeading !== undefined ||
      commands?.setHeadingAccent !== undefined ||
      commands?.deleteHeading !== undefined
    );
  }

  protected canRename(moduleId: string): boolean {
    return this.commandsFor(moduleId)?.renameHeading !== undefined;
  }

  protected canMoveHeading(moduleId: string): boolean {
    return this.commandsFor(moduleId)?.moveHeading !== undefined;
  }

  protected canSetAccent(moduleId: string): boolean {
    return this.commandsFor(moduleId)?.setHeadingAccent !== undefined;
  }

  protected canDeleteHeading(moduleId: string): boolean {
    return this.commandsFor(moduleId)?.deleteHeading !== undefined;
  }

  protected canReset(moduleId: string): boolean {
    return !this.rail() && this.commandsFor(moduleId)?.reset !== undefined;
  }

  /** The accents a user may choose for a heading; `marked` is the module's own. */
  protected readonly accentChoices: readonly SaphirNavigationAccent[] = ['warm', 'alert'];

  /**
   * Which inline panel is open, if any. One at a time: the column is 15.5rem
   * wide and two open panels would leave no menu to reason about.
   */
  protected readonly openPanel = signal<string | null>(null);

  /** The control that opened the panel, so focus can return to it (FR-026). */
  private panelTrigger: HTMLElement | null = null;

  protected togglePanel(key: string, event: Event): void {
    const open = this.openPanel() === key;

    this.panelTrigger = open ? null : (event.currentTarget as HTMLElement);
    this.openPanel.set(open ? null : key);
  }

  /**
   * Close the open panel and put focus back where it came from.
   *
   * Escape and a click outside both land here. The standalone menu does not
   * do this today; the shell must not inherit the gap.
   */
  protected closePanel(): void {
    if (this.openPanel() === null) {
      return;
    }

    this.openPanel.set(null);
    this.panelTrigger?.focus();
    this.panelTrigger = null;
  }

  protected isPanelOpen(key: string): boolean {
    return this.openPanel() === key;
  }

  /** A press that lands outside the open panel closes it. */
  protected onDocumentPointerDown(event: PointerEvent): void {
    if (this.openPanel() === null) {
      return;
    }

    const target = event.target;

    if (
      target instanceof Node &&
      (target as Element).closest?.('.sidebar__arrange, .sidebar__arrange-trigger')
    ) {
      return;
    }

    this.closePanel();
  }

  protected moveEntry(moduleId: string, entryId: string, headingId: string, index: number): void {
    this.commandsFor(moduleId)?.moveEntry?.(entryId, headingId, index);
    this.closePanel();
  }

  protected renameHeading(moduleId: string, headingId: string, title: string): void {
    const name = title.trim();

    if (name) {
      this.commandsFor(moduleId)?.renameHeading?.(headingId, name);
    }

    this.closePanel();
  }

  protected moveHeading(moduleId: string, headingId: string, direction: -1 | 1): void {
    this.commandsFor(moduleId)?.moveHeading?.(headingId, direction);
    this.closePanel();
  }

  protected setHeadingAccent(
    moduleId: string,
    headingId: string,
    accent: SaphirNavigationAccent | null,
  ): void {
    this.commandsFor(moduleId)?.setHeadingAccent?.(headingId, accent);
    this.closePanel();
  }

  protected deleteHeading(moduleId: string, headingId: string): void {
    // No confirmation: deleting a heading loses nothing — its entries fall
    // back to the fixed heading, and the user can see that they did.
    this.commandsFor(moduleId)?.deleteHeading?.(headingId);
    this.closePanel();
  }

  protected createHeading(moduleId: string, title: string): void {
    const name = title.trim();

    if (name) {
      this.commandsFor(moduleId)?.createHeading?.(name);
    }

    this.closePanel();
  }

  protected resetMenu(moduleId: string): void {
    this.commandsFor(moduleId)?.reset?.();
    this.closePanel();
  }

  /** Where an entry may be moved to, and where it currently sits. */
  /**
   * Where an entry may be moved to.
   *
   * A heading whose content the module pins (`entriesFixed`) is left out: it
   * holds navigation rather than arrangement — the way into another module —
   * and offering it as a destination would promise a move that cannot happen.
   */
  protected headingsOfModule(moduleId: string): readonly SaphirNavigationHeading[] {
    const headings = this.modules().find((module) => module.id === moduleId)?.headings ?? [];

    return headings.filter((heading) => !heading.entriesFixed);
  }

  protected isFirstIn(heading: SaphirNavigationHeading, item: SaphirNavigationItem): boolean {
    return heading.entries.length > 0 && entryKeyOf(heading.entries[0]) === entryKeyOf(item);
  }

  protected isLastIn(heading: SaphirNavigationHeading, item: SaphirNavigationItem): boolean {
    const last = heading.entries.at(-1);

    return last !== undefined && entryKeyOf(last) === entryKeyOf(item);
  }

  protected indexIn(heading: SaphirNavigationHeading, item: SaphirNavigationItem): number {
    return heading.entries.findIndex((entry) => entryKeyOf(entry) === entryKeyOf(item));
  }

  protected isFirstHeading(moduleId: string, heading: SaphirNavigationHeading): boolean {
    const headings = this.headingsOfModule(moduleId).filter((h) => !h.fixed);

    return headings.length > 0 && headings[0].id === heading.id;
  }

  protected isLastHeading(moduleId: string, heading: SaphirNavigationHeading): boolean {
    const headings = this.headingsOfModule(moduleId).filter((h) => !h.fixed);

    return headings.at(-1)?.id === heading.id;
  }

  // ── Dragging ──────────────────────────────────────────────────────────────

  /** The entry being dragged, and where it would land. */
  protected readonly dragging = signal<string | null>(null);
  protected readonly dropTarget = signal<{ key: string; after: boolean } | null>(null);
  protected readonly dropHeading = signal<string | null>(null);

  protected onDragStart(event: DragEvent, item: SaphirNavigationItem): void {
    this.dragging.set(entryKeyOf(item));
    // No ghost image, no tilt: the thing being moved is a row in a list, and
    // the feedback that matters is where it will land.
    event.dataTransfer?.setData('text/plain', entryKeyOf(item));

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  protected onDragEnd(): void {
    this.dragging.set(null);
    this.dropTarget.set(null);
    this.dropHeading.set(null);
  }

  /**
   * A row's own `dragover`, once it has placed the insertion bar.
   *
   * The rows sit INSIDE the heading, so every `dragover` over a row reaches
   * the heading too, right after. Without this mark the heading would then
   * clear the bar the row just set and the drop would append at the end —
   * the pointer over the third row, the entry landing twelfth.
   */
  private static readonly HANDLED = Symbol('dragover-handled');

  /**
   * Which half of the row the pointer is in decides before or after — the
   * insertion bar then shows exactly where the entry lands.
   */
  protected onDragOverEntry(event: DragEvent, item: SaphirNavigationItem, headingId: string): void {
    if (this.dragging() === null) {
      return;
    }

    event.preventDefault();
    (event as unknown as Record<symbol, boolean>)[ShellSidebar.HANDLED] = true;

    const row = event.currentTarget as HTMLElement;
    const bounds = row.getBoundingClientRect();

    this.dropTarget.set({
      key: entryKeyOf(item),
      after: event.clientY > bounds.top + bounds.height / 2,
    });
    this.dropHeading.set(headingId);
  }

  protected onDragOverHeading(event: DragEvent, headingId: string): void {
    if (this.dragging() === null) {
      return;
    }

    event.preventDefault();
    this.dropHeading.set(headingId);

    // Over one of its own rows the row decides where the bar goes; only the
    // bare heading — its caption, or the space past the last row — means
    // "append".
    if ((event as unknown as Record<symbol, boolean>)[ShellSidebar.HANDLED]) {
      return;
    }

    this.dropTarget.set(null);
  }

  protected isDropBefore(item: SaphirNavigationItem): boolean {
    const target = this.dropTarget();

    return target?.key === entryKeyOf(item) && !target.after;
  }

  protected isDropAfter(item: SaphirNavigationItem): boolean {
    const target = this.dropTarget();

    return target?.key === entryKeyOf(item) && target.after;
  }

  /** Drop onto a heading or between its entries. */
  protected onDrop(event: DragEvent, moduleId: string, heading: SaphirNavigationHeading): void {
    event.preventDefault();
    // A drop on a row reaches the heading next; the row has already moved the
    // entry, and the heading must not move it a second time.
    event.stopPropagation();

    const entryId = this.dragging();
    const target = this.dropTarget();

    if (entryId === null) {
      this.onDragEnd();
      return;
    }

    // Dropped on the heading itself, or past the last row: append.
    let index = heading.entries.length;

    if (target !== null) {
      const at = heading.entries.findIndex((entry) => entryKeyOf(entry) === target.key);

      if (at >= 0) {
        index = target.after ? at + 1 : at;
      }
    }

    this.moveEntry(moduleId, entryId, heading.id, index);
    this.onDragEnd();
  }

  /** Does any leaf below `item` point at the screen currently shown? */
  private containsActive(moduleId: string, item: SaphirNavigationItem): boolean {
    const url = this.currentPath();

    if (item.route !== undefined) {
      const target = this.link(moduleId, item.route).join('/').replace(/^\/\//, '/');

      return item.route === '' ? url === target : url === target || url.startsWith(`${target}/`);
    }

    return (item.children ?? []).some((child) => this.containsActive(moduleId, child));
  }

  protected expanded(moduleId: string): boolean {
    return this.overrides()[moduleId] ?? this.activeId() === moduleId;
  }

  protected toggle(moduleId: string): void {
    const next = !this.expanded(moduleId);

    this.overrides.update((overrides) => ({ ...overrides, [moduleId]: next }));
  }

  /** `/modules/<id>/<route>`; the module root when the route is empty. */
  protected link(moduleId: string, route?: string): string[] {
    const base = ['/', this.prefix, moduleId];

    return route ? [...base, ...route.split('/')] : base;
  }
}

const FOLD_STORAGE_KEY = 'saphir-web.sidebar-folds';

/**
 * The fold choices from the last session, keyed by entry or heading id.
 *
 * Restored WITHOUT the address each was made on: that address belonged to a
 * session that has ended, and keeping it would make a stale comparison. An
 * empty address means a manual fold holds until the user navigates into what
 * it hides — which is the rule anyway.
 *
 * Storage is unavailable in private windows and behind some policies, and
 * what comes back may be anything at all. Losing a fold is a small thing;
 * failing to draw the menu is not, so every failure path ends in `{}`.
 */
function restoreFolds(): Record<string, { open: boolean; url: string }> {
  try {
    const raw = localStorage.getItem(FOLD_STORAGE_KEY);

    if (!raw) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }

    const folds: Record<string, { open: boolean; url: string }> = {};

    for (const [key, open] of Object.entries(parsed)) {
      // Headings only, and group entries written by an earlier build are
      // dropped rather than honoured: a stored `true` on a group would open
      // it on every load, which is the behaviour this ignores on purpose.
      if (typeof open === 'boolean' && isHeadingKey(key)) {
        folds[key] = { open, url: '' };
      }
    }

    return folds;
  } catch {
    return {};
  }
}

/**
 * Is this the key of a HEADING rather than of a group inside one?
 *
 * `headingKey` composes `<module>/heading:<id>` and nothing follows it; a
 * group nested under that heading appends its own segment
 * (`…/heading:auftragsbearbeitung/group:konstantendaten`), so the test is
 * that `heading:` opens the last segment.
 */
function isHeadingKey(key: string): boolean {
  return key.slice(key.lastIndexOf('/') + 1).startsWith('heading:');
}

/**
 * Only HEADINGS are remembered past the session.
 *
 * A heading is a section the user arranged and expects to find as they left
 * it (FR-023). A group inside it is a fold-out whose choice expires with the
 * screen it was made on — storing that would write a value that is ignored on
 * the next load anyway, and the stored `true` is what kept `Konstantendaten`
 * spilling 34 entries into the column session after session.
 */
function persistFolds(folds: Record<string, { open: boolean; url: string }>): void {
  try {
    const flat: Record<string, boolean> = {};

    for (const [key, value] of Object.entries(folds)) {
      if (isHeadingKey(key)) {
        flat[key] = value.open;
      }
    }

    localStorage.setItem(FOLD_STORAGE_KEY, JSON.stringify(flat));
  } catch {
    // Ignored on purpose — see `restoreFolds`.
  }
}

/** The module a URL points into, or `undefined` on the shell's own screens. */
function moduleIdFromUrl(url: string): string | undefined {
  const [, prefix, id] = url.split(/[?#]/)[0].split('/');

  return prefix === MODULE_ROUTE_PREFIX && id ? decodeURIComponent(id) : undefined;
}
